/**
 * The customer card's invoices, grouped year → month.
 *
 * A customer with years of history is unreadable as one flat list, and the
 * first figure people ask for is "how much did we invoice them that month", so
 * every branch carries its own sums (with VAT / without VAT / debt) next to the
 * invoice count.
 *
 * Grouping goes through the SAME Europe/Vilnius formatting as the date cell
 * (`formatPrettyDate`), so an invoice can never land in a month other than the
 * one its own row displays.
 *
 * Only the customer card asks for this view: the company-wide debt list renders
 * the same invoices flat (see Invoices.tsx), because a working list of unpaid
 * invoices must not start out collapsed.
 *
 * Invoices can also be picked with checkboxes — a branch checkbox takes every
 * invoice under it — and the sums over the picked ones show up in the same
 * sticky bottom bar the customers list uses.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  FileText,
  Pencil,
} from "lucide-react";
import useRestorableState from "hooks/useRestorableState";
import { Badge } from "catalyst-ui-kit/badge";
import { Invoice } from "types/Invoice";
import Currency from "components/format/currency/currency";
import { formatPrettyDate } from "utils/helperFunctions";
import useIsPhone from "features/files/use-is-phone";
import {
  formatInvoicePublicNumber,
  getInvoiceFileId,
} from "utils/invoice-number";
import { formatPeriodMonth } from "features/billing-period/billing-period";
import InvoicesSelectionBar from "./invoices-selection-bar";

type Props = {
  invoices: Invoice[];
  onOpenProducts: (invoice: Invoice) => void;
  /** ERP rows only — Finvalda invoices are not editable in the ERP. */
  onOpenInvoice?: (invoice: Invoice) => void;
  onViewPdf?: (fileId: string) => void;
  getDownloadUrl?: (fileId: string) => string;
};

type Totals = {
  count: number;
  amountWithVat: number;
  amount: number;
  debt: number;
};

type MonthNode = {
  key: string;
  label: string;
  totals: Totals;
  invoices: Invoice[];
};

type YearNode = {
  key: string;
  label: string;
  totals: Totals;
  months: MonthNode[];
};

const UNKNOWN = "unknown";
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const toNumber = (value: unknown): number => {
  const parsed =
    typeof value === "number" ? value : parseFloat(value as string);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Money is only ever meaningful to the cent, but summing floats drifts a hair
 * past it (e.g. two amounts that net to exactly 0 land on -0.0000000000004
 * instead) — and `Intl` keeps the minus sign on a negative number even once
 * it rounds to "0.00", so a branch that nets to nothing renders "-€0.00".
 * Rounding to the cent and folding -0 back to 0 (they compare equal, so this
 * only changes which zero it is) fixes the display without touching any
 * total that isn't actually zero.
 */
const roundMoney = (value: number): number => {
  const rounded = Math.round(value * 100) / 100;
  return rounded === 0 ? 0 : rounded;
};

/**
 * The stored `_debt` when there is one, otherwise the same figure the backend
 * would have stored: gross minus paid (InvoicesService.calculateSummaryByInvoiceId).
 * `_debt` is filled in by the invoice-items sync, so an invoice whose lines
 * never arrived has none — and used to render "-" in a debt column. The same
 * value feeds the row and the branch sums, so a branch total is always the sum
 * of the numbers visible underneath it.
 */
const invoiceDebt = (invoice: Invoice): number =>
  roundMoney(
    typeof invoice._debt === "number" && Number.isFinite(invoice._debt)
      ? invoice._debt
      : toNumber(invoice._amountWithVat) - toNumber(invoice._paidAmount)
  );

const emptyTotals = (): Totals => ({
  count: 0,
  amountWithVat: 0,
  amount: 0,
  debt: 0,
});

const addToTotals = (totals: Totals, invoice: Invoice) => {
  totals.count += 1;
  totals.amountWithVat = roundMoney(
    totals.amountWithVat + toNumber(invoice._amountWithVat)
  );
  totals.amount = roundMoney(totals.amount + toNumber(invoice._amount));
  totals.debt = roundMoney(totals.debt + invoiceDebt(invoice));
};

const monthName = (monthKey: string, locale: string) => {
  const [year, month] = monthKey.split("-").map(Number);
  const name = new Intl.DateTimeFormat(locale, {
    month: "long",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, 1)));
  return `${monthKey.slice(5)} · ${name.charAt(0).toUpperCase()}${name.slice(
    1
  )}`;
};

const buildTree = (
  invoices: Invoice[],
  locale: string,
  unknownLabel: string
): YearNode[] => {
  const years = new Map<
    string,
    {
      totals: Totals;
      months: Map<string, { totals: Totals; invoices: Invoice[] }>;
    }
  >();

  invoices.forEach((invoice) => {
    const isoDate = formatPrettyDate(invoice.date);
    const parsed = ISO_DATE.exec(isoDate);
    const yearKey = parsed ? parsed[1] : UNKNOWN;
    const monthKey = parsed ? `${parsed[1]}-${parsed[2]}` : UNKNOWN;

    if (!years.has(yearKey)) {
      years.set(yearKey, { totals: emptyTotals(), months: new Map() });
    }
    const year = years.get(yearKey)!;
    if (!year.months.has(monthKey)) {
      year.months.set(monthKey, { totals: emptyTotals(), invoices: [] });
    }
    const month = year.months.get(monthKey)!;

    addToTotals(year.totals, invoice);
    addToTotals(month.totals, invoice);
    month.invoices.push(invoice);
  });

  // Newest first — an unparseable date has no place on the timeline, so it sinks
  // to the bottom instead of pretending to be year 0.
  const byKeyDesc = (a: string, b: string) => {
    if (a === UNKNOWN) return 1;
    if (b === UNKNOWN) return -1;
    return b.localeCompare(a);
  };

  return Array.from(years.entries())
    .sort(([a], [b]) => byKeyDesc(a, b))
    .map(([yearKey, year]) => ({
      key: `y-${yearKey}`,
      label: yearKey === UNKNOWN ? unknownLabel : yearKey,
      totals: year.totals,
      months: Array.from(year.months.entries())
        .sort(([a], [b]) => byKeyDesc(a, b))
        .map(([monthKey, month]) => ({
          key: `m-${monthKey}`,
          label:
            monthKey === UNKNOWN ? unknownLabel : monthName(monthKey, locale),
          totals: month.totals,
          invoices: [...month.invoices].sort((a, b) => {
            const left = formatPrettyDate(a.date);
            const right = formatPrettyDate(b.date);
            return right.localeCompare(left);
          }),
        })),
    }));
};

/** `indeterminate` is a DOM property, not an attribute, so it needs the ref. */
const SelectCheckbox = ({
  checked,
  indeterminate = false,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  ariaLabel: string;
}) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  // The label around the 16 px box is what a finger hits on a phone.
  return (
    <label className="inline-flex items-center max-md:p-3.5">
      <input
        ref={ref}
        type="checkbox"
        className="h-4 w-4 rounded border-gray-300 text-[#3F3227] focus:ring-[#3F3227]"
        checked={checked}
        onChange={onChange}
        onClick={(event) => event.stopPropagation()}
        aria-label={ariaLabel}
      />
    </label>
  );
};

/**
 * The nearest ancestor the user can scroll (overflow-y auto/scroll), or <body>
 * when the page itself scrolls. `hidden`/`clip` ancestors (the Polaris Card)
 * are skipped — they clip, they do not scroll.
 */
const scrollParentOf = (el: HTMLElement | null): HTMLElement => {
  let node = el?.parentElement ?? null;
  while (node && node !== document.body) {
    if (/(auto|scroll|overlay)/.test(window.getComputedStyle(node).overflowY)) {
      return node;
    }
    node = node.parentElement;
  }
  return document.body;
};

const InvoicesTree = ({
  invoices,
  onOpenProducts,
  onOpenInvoice,
  onViewPdf,
  getDownloadUrl,
}: Props) => {
  const { t, i18n } = useTranslation();
  const isPhone = useIsPhone();
  const locale = (i18n as any).language === "lt" ? "lt-LT" : "en-US";

  // Picked invoices, parked per path like the open branches: coming back to the
  // tab finds the same selection, a new tab starts clean.
  const [selectedIds, setSelectedIds] = useRestorableState<string[]>(
    "customer-invoices-selected",
    []
  );
  // View-only state — deliberately not parked, so Back never lands on a tree
  // filtered down to a selection the user cannot see the rest of.
  const [showOnlySelected, setShowOnlySelected] = useState(false);

  const allInvoices = useMemo(() => invoices || [], [invoices]);

  // A parked id whose invoice is no longer in the list counts for nothing: the
  // bar would otherwise report "3 selected" over the sums of the two that are
  // still here, and "show only selected" could filter the tree down to nothing.
  const selected = useMemo(() => {
    const present = new Set(allInvoices.map((invoice) => invoice.id));
    return new Set(selectedIds.filter((id) => present.has(id)));
  }, [selectedIds, allInvoices]);
  const selectedCount = selected.size;

  // The bar is portaled to <body> (see invoices-selection-bar.tsx) so it can
  // clear the Polaris Card's stacking context — which means a spacer sized to
  // it can no longer live inside this card either, or it would reserve space
  // that isn't under the bar once the right-column cards run longer or the
  // bar wraps onto extra rows on a narrow screen. Reserve real page space
  // instead: pad the element the page actually scrolls in by the bar's
  // measured height, and drop the padding as soon as the bar hides or this
  // tree unmounts. That element is not <body>: DashboardLayout pins the shell
  // to the viewport and scrolls `.dashboard-main`, so padding <body> would
  // only add a second scrollbar and leave the last cards under the bar.
  const barRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  // Keyed on visibility, not on the count: the bar stays the same element
  // while picks are added, and re-running on every pick would clear the
  // padding and re-apply it, making the page jump. A layout effect so the
  // padding lands in the same frame the bar first appears.
  const barVisible = selectedCount > 0;
  useLayoutEffect(() => {
    if (!barVisible) return undefined;
    const barEl = barRef.current;
    if (!barEl) return undefined;
    const scroller = scrollParentOf(anchorRef.current);

    // `removeProperty` rather than assigning "" — jsdom (unlike real
    // browsers) does not clear a previously-set value on an empty-string
    // assignment, and removeProperty works everywhere.
    const clearPadding = () => scroller.style.removeProperty("padding-bottom");
    const applyPadding = (height: number) => {
      if (height > 0) {
        scroller.style.paddingBottom = `${height}px`;
      } else {
        clearPadding();
      }
    };
    applyPadding(barEl.offsetHeight);

    if (typeof ResizeObserver === "undefined") return clearPadding;

    const resizeObserver = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height ?? barEl.offsetHeight;
      applyPadding(height);
    });
    resizeObserver.observe(barEl);

    return () => {
      resizeObserver.disconnect();
      clearPadding();
    };
  }, [barVisible]);

  // Sums over the whole selection, not over what the current view renders.
  const selectionTotals = useMemo(() => {
    const totals = emptyTotals();
    allInvoices.forEach((invoice) => {
      if (selected.has(invoice.id)) addToTotals(totals, invoice);
    });
    return totals;
  }, [allInvoices, selected]);

  const shownInvoices = useMemo(
    () =>
      showOnlySelected
        ? allInvoices.filter((invoice) => selected.has(invoice.id))
        : allInvoices,
    [allInvoices, showOnlySelected, selected]
  );

  const tree = useMemo(
    () => buildTree(shownInvoices, locale, t("unknown_period")),
    [shownInvoices, locale, t]
  );

  // The selection can empty out from under the filter (Clear, or unticking the
  // last row) — fall back to the full tree instead of an empty screen.
  useEffect(() => {
    if (showOnlySelected && selectedCount === 0) {
      setShowOnlySelected(false);
    }
  }, [showOnlySelected, selectedCount]);

  const grandTotals = useMemo(
    () =>
      tree.reduce((acc, year) => {
        acc.count += year.totals.count;
        acc.amountWithVat = roundMoney(
          acc.amountWithVat + year.totals.amountWithVat
        );
        acc.amount = roundMoney(acc.amount + year.totals.amount);
        acc.debt = roundMoney(acc.debt + year.totals.debt);
        return acc;
      }, emptyTotals()),
    [tree]
  );

  const allKeys = useMemo(
    () => tree.flatMap((year) => [year.key, ...year.months.map((m) => m.key)]),
    [tree]
  );

  // null = the user has not touched the tree yet, so the newest year opens
  // itself once the data arrives (the customer is still loading on first
  // render). Parked per path like the other list state, so Back finds the same
  // branches open.
  const [openKeys, setOpenKeys] = useRestorableState<string[] | null>(
    "customer-invoices-open",
    null
  );
  const defaultOpen = useMemo(() => (tree.length ? [tree[0].key] : []), [tree]);
  const open = useMemo(
    // While the tree is filtered down to the selection there is nothing worth
    // hiding — every remaining row is one the user picked.
    () => new Set(showOnlySelected ? allKeys : openKeys ?? defaultOpen),
    [showOnlySelected, allKeys, openKeys, defaultOpen]
  );
  const isAllOpen = allKeys.length > 0 && allKeys.every((key) => open.has(key));

  const toggle = (key: string) => {
    // While the tree is filtered to the selection every branch is forced open,
    // so a click could only rewrite the parked open set behind the user's back
    // and surprise them once the filter goes off.
    if (showOnlySelected) return;
    setOpenKeys((previous) => {
      const current = previous ?? defaultOpen;
      return current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key];
    });
  };

  const toggleAll = () => setOpenKeys(isAllOpen ? [] : allKeys);

  const setManySelected = (ids: string[], checked: boolean) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return Array.from(next);
    });
  };

  const branchInvoiceIds = (node: YearNode | MonthNode): string[] =>
    "months" in node
      ? node.months.flatMap((month) =>
          month.invoices.map((invoice) => invoice.id)
        )
      : node.invoices.map((invoice) => invoice.id);

  /** Ticked when every invoice under the node is picked, dashed when some are. */
  const branchSelection = (node: YearNode | MonthNode) => {
    const ids = branchInvoiceIds(node);
    const picked = ids.filter((id) => selected.has(id)).length;
    return {
      ids,
      checked: ids.length > 0 && picked === ids.length,
      indeterminate: picked > 0 && picked < ids.length,
    };
  };

  const branchCheckbox = (node: YearNode | MonthNode) => {
    const { ids, checked, indeterminate } = branchSelection(node);
    return (
      <SelectCheckbox
        checked={checked}
        indeterminate={indeterminate}
        onChange={() => setManySelected(ids, !checked)}
        ariaLabel={`${t("select_row")} ${node.label}`}
      />
    );
  };

  const invoiceCheckbox = (invoice: Invoice) => (
    <SelectCheckbox
      checked={selected.has(invoice.id)}
      onChange={() => setManySelected([invoice.id], !selected.has(invoice.id))}
      ariaLabel={`${t("select_row")} ${invoice.number || ""}`.trim()}
    />
  );

  const shownIds = shownInvoices.map((invoice) => invoice.id);
  const pickedShownCount = shownIds.filter((id) => selected.has(id)).length;

  /** ERP badge only — a Finvalda row needs no label, it is the default. */
  const typeBadge = (invoice: Invoice) =>
    invoice.type === "erp" ? (
      <Badge color="emerald">
        {t("invoice_type_erp", { defaultValue: "ERP" })}
      </Badge>
    ) : null;

  const numberCell = (invoice: Invoice) => (
    <>
      <span className="inline-flex items-center gap-2">
        <span>{formatInvoicePublicNumber(invoice) || invoice.number || "-"}</span>
        {typeBadge(invoice)}
      </span>
      {invoice.customerName ? (
        <span className="ml-2 text-xs text-gray-500">
          {invoice.customerName}
        </span>
      ) : null}
      {invoice.periodFrom ? (
        <div className="text-xs text-gray-500">
          {formatPeriodMonth(invoice.periodFrom)}
        </div>
      ) : null}
    </>
  );

  const desktopActionBtnCls =
    "inline-flex h-8 w-8 items-center justify-center rounded-sm border border-[#E6E1DB] bg-white text-[#3F3227] hover:bg-gray-50";
  const mobileActionBtnCls =
    "inline-flex h-11 w-11 items-center justify-center rounded-sm border border-[#E6E1DB] bg-white text-[#3F3227] hover:bg-gray-50";

  /** Edit (ERP only), View PDF and Download — all optional, all row-scoped. */
  const rowActions = (invoice: Invoice, iconCls: string) => {
    const fileId = getInvoiceFileId(invoice);
    const canEdit = !!onOpenInvoice && invoice.type === "erp";
    if (!canEdit && !fileId) return null;

    return (
      <>
        {canEdit && (
          <button
            type="button"
            title={t("edit")}
            className={iconCls}
            onClick={() => onOpenInvoice!(invoice)}
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
        {fileId && onViewPdf && (
          <button
            type="button"
            title={t("view")}
            className={iconCls}
            onClick={() => onViewPdf(fileId)}
          >
            <FileText className="h-4 w-4" />
          </button>
        )}
        {fileId && getDownloadUrl && (
          <a
            href={getDownloadUrl(fileId)}
            target="_blank"
            rel="noopener noreferrer"
            title={t("download")}
            className={iconCls}
          >
            <Download className="h-4 w-4" />
          </a>
        )}
      </>
    );
  };

  const selectionBar = (
    <>
      {/* Marks where the tree sits, to find what the page scrolls in. */}
      <span ref={anchorRef} hidden />
      <InvoicesSelectionBar
        ref={barRef}
        count={selectedCount}
        amountWithVat={selectionTotals.amountWithVat}
        amount={selectionTotals.amount}
        debt={selectionTotals.debt}
        showOnlySelected={showOnlySelected}
        onToggleShowOnlySelected={() => setShowOnlySelected((value) => !value)}
        onClear={() => setSelectedIds([])}
      />
    </>
  );

  const countPill = (count: number) => (
    <span className="ml-2 text-xs font-normal text-gray-500">{count}</span>
  );

  const branchToggle = (
    node: YearNode | MonthNode,
    level: "year" | "month"
  ) => (
    <button
      type="button"
      onClick={() => toggle(node.key)}
      disabled={showOnlySelected}
      aria-expanded={open.has(node.key)}
      className={clsx(
        "inline-flex items-center gap-1 text-left text-gray-900 max-md:min-h-11",
        !showOnlySelected && "hover:text-[#B54708]",
        level === "year" ? "font-semibold" : "font-medium"
      )}
    >
      {open.has(node.key) ? (
        <ChevronDown className="h-4 w-4 shrink-0" />
      ) : (
        <ChevronRight className="h-4 w-4 shrink-0" />
      )}
      <span>{node.label}</span>
      {countPill(node.totals.count)}
    </button>
  );

  // Pointless while the tree is filtered to the selection — everything is open
  // there by definition, so the button would look broken.
  const allToggle = allKeys.length > 0 && !showOnlySelected && (
    <div className="mb-2 flex justify-end">
      <button
        type="button"
        onClick={toggleAll}
        className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#B54708]"
      >
        {isAllOpen ? (
          <ChevronRight className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
        {isAllOpen ? t("collapse_all") : t("expand_all")}
      </button>
    </div>
  );

  const emptyState = (
    <div className="rounded-sm border border-[#E6E1DB] bg-white px-4 py-12 text-center text-sm text-gray-500">
      {t("no_data")}
    </div>
  );

  if (isPhone) {
    if (!tree.length) return emptyState;

    const amountsBlock = (totals: Totals) => (
      <div className="mt-1 flex flex-col items-end text-xs text-gray-600">
        <span>
          {t("totalWithVat")}:{" "}
          <Currency value={totals.amountWithVat} isSymbol isDecimal />
        </span>
        <span>
          {t("debt")}: <Currency value={totals.debt} isSymbol isDecimal />
        </span>
      </div>
    );

    return (
      <>
        {allToggle}
        <div className="mb-3 flex items-start justify-between rounded-sm border border-[#E6E1DB] bg-gray-50 px-3 py-2">
          <span className="font-semibold text-gray-900">
            {t("total")}
            {countPill(grandTotals.count)}
          </span>
          {amountsBlock(grandTotals)}
        </div>
        {tree.map((year) => (
          <div key={year.key} className="mb-2">
            <div className="flex items-start justify-between rounded-sm border border-[#E6E1DB] bg-gray-100 px-3 py-2">
              <span className="flex items-center gap-2">
                {branchCheckbox(year)}
                {branchToggle(year, "year")}
              </span>
              {amountsBlock(year.totals)}
            </div>
            {open.has(year.key) &&
              year.months.map((month) => (
                <div key={month.key} className="ml-3 mt-1">
                  <div className="flex items-start justify-between rounded-sm border border-[#E6E1DB] bg-gray-50 px-3 py-2">
                    <span className="flex items-center gap-2">
                      {branchCheckbox(month)}
                      {branchToggle(month, "month")}
                    </span>
                    {amountsBlock(month.totals)}
                  </div>
                  {open.has(month.key) &&
                    month.invoices.map((invoice) => (
                      <div
                        key={invoice.id}
                        className="ml-3 mt-1 rounded-sm border border-[#E6E1DB] bg-white p-3"
                      >
                        <div className="flex justify-between">
                          <span className="flex items-center gap-2 text-sm text-gray-700">
                            {invoiceCheckbox(invoice)}
                            {invoice.customerName}
                          </span>
                          <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                            {formatInvoicePublicNumber(invoice) ||
                              invoice.number ||
                              "-"}
                            {typeBadge(invoice)}
                          </span>
                        </div>
                        {invoice.periodFrom ? (
                          <div className="text-right text-xs text-gray-500">
                            {formatPeriodMonth(invoice.periodFrom)}
                          </div>
                        ) : null}
                        <div className="mt-1 flex justify-between text-sm">
                          <Currency
                            value={invoice._amountWithVat}
                            isSymbol
                            isDecimal
                          />
                          <span>
                            {t("debt")}:{" "}
                            <Currency
                              value={invoiceDebt(invoice)}
                              isSymbol
                              isDecimal
                            />
                          </span>
                        </div>
                        <div className="mt-1 flex justify-between text-xs text-gray-500">
                          <span>{formatPrettyDate(invoice.date)}</span>
                          <span>
                            {formatPrettyDate(invoice.paymentDueDate)}
                          </span>
                        </div>
                        {(!!invoice.products?.length ||
                          rowActions(invoice, mobileActionBtnCls)) && (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {!!invoice.products?.length && (
                              <button
                                type="button"
                                className="inline-flex min-h-11 items-center gap-2 rounded-sm border border-[#E6E1DB] bg-white px-3 py-1.5 text-sm text-[#3F3227] hover:bg-gray-50"
                                onClick={() => onOpenProducts(invoice)}
                              >
                                <Eye className="h-4 w-4" />
                                {t("view_products")}
                              </button>
                            )}
                            {rowActions(invoice, mobileActionBtnCls)}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              ))}
          </div>
        ))}
        {selectionBar}
      </>
    );
  }

  const money = (value: number) => (
    <Currency value={value} isSymbol isDecimal />
  );

  const branchRow = (node: YearNode | MonthNode, level: "year" | "month") => (
    <tr
      key={node.key}
      data-testid={`invoices-branch-${node.key}`}
      className={level === "year" ? "bg-gray-100" : "bg-gray-50"}
    >
      <td className="px-4 py-2.5">{branchCheckbox(node)}</td>
      <td
        className={clsx(
          "px-4 py-2.5 text-sm text-gray-900",
          level === "month" && "pl-10"
        )}
        colSpan={2}
      >
        {branchToggle(node, level)}
      </td>
      <td className="px-4 py-2.5 text-right text-sm font-medium text-gray-900">
        {money(node.totals.amountWithVat)}
      </td>
      <td className="px-4 py-2.5 text-right text-sm font-medium text-gray-900">
        {money(node.totals.amount)}
      </td>
      <td className="px-4 py-2.5 text-right text-sm font-medium text-gray-900">
        {money(node.totals.debt)}
      </td>
      <td className="px-4 py-2.5" />
      <td className="px-4 py-2.5" />
      <td className="px-4 py-2.5" />
    </tr>
  );

  return (
    <>
      {allToggle}
      {tree.length === 0 ? (
        emptyState
      ) : (
        <div
          className="overflow-hidden rounded-sm border border-[#E6E1DB] bg-white"
          data-scroll-restore="invoices-tree"
        >
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr className="bg-gray-50">
                  <th className="w-10 border-b border-[#E4E3E1] bg-[#F9FAFB] px-4 py-3 text-left">
                    <SelectCheckbox
                      checked={
                        shownIds.length > 0 &&
                        pickedShownCount === shownIds.length
                      }
                      indeterminate={
                        pickedShownCount > 0 &&
                        pickedShownCount < shownIds.length
                      }
                      onChange={() =>
                        setManySelected(
                          shownIds,
                          pickedShownCount !== shownIds.length
                        )
                      }
                      ariaLabel={t("select_all_rows")}
                    />
                  </th>
                  <th className="border-b border-[#E4E3E1] bg-[#F9FAFB] px-4 py-3 text-left text-sm font-normal text-gray-600">
                    {t("period")} / {t("number")}
                  </th>
                  <th className="border-b border-[#E4E3E1] bg-[#F9FAFB] px-4 py-3 text-left text-sm font-normal text-gray-600">
                    {t("date")}
                  </th>
                  <th className="border-b border-[#E4E3E1] bg-[#F9FAFB] px-4 py-3 text-right text-sm font-normal text-gray-600">
                    {t("totalWithVat")}
                  </th>
                  <th className="border-b border-[#E4E3E1] bg-[#F9FAFB] px-4 py-3 text-right text-sm font-normal text-gray-600">
                    {t("totalWithoutVat")}
                  </th>
                  <th className="border-b border-[#E4E3E1] bg-[#F9FAFB] px-4 py-3 text-right text-sm font-normal text-gray-600">
                    {t("debt")}
                  </th>
                  <th className="border-b border-[#E4E3E1] bg-[#F9FAFB] px-4 py-3 text-left text-sm font-normal text-gray-600">
                    {t("payment_due_date")}
                  </th>
                  <th className="border-b border-[#E4E3E1] bg-[#F9FAFB] px-4 py-3 text-left text-sm font-normal text-gray-600">
                    {t("products")}
                  </th>
                  <th className="border-b border-[#E4E3E1] bg-[#F9FAFB] px-4 py-3 text-left text-sm font-normal text-gray-600">
                    {t("actions")}
                  </th>
                </tr>
                <tr className="bg-gray-50">
                  <th className="border-b border-[#E4E3E1] bg-[#F9FAFB] px-4 py-3" />
                  <th
                    className="border-b border-[#E4E3E1] bg-[#F9FAFB] px-4 py-3 text-left text-sm font-semibold text-gray-900"
                    colSpan={2}
                  >
                    {t("total")}
                    {countPill(grandTotals.count)}
                  </th>
                  <th className="border-b border-[#E4E3E1] bg-[#F9FAFB] px-4 py-3 text-right text-sm font-semibold text-gray-900">
                    {money(grandTotals.amountWithVat)}
                  </th>
                  <th className="border-b border-[#E4E3E1] bg-[#F9FAFB] px-4 py-3 text-right text-sm font-semibold text-gray-900">
                    {money(grandTotals.amount)}
                  </th>
                  <th className="border-b border-[#E4E3E1] bg-[#F9FAFB] px-4 py-3 text-right text-sm font-semibold text-gray-900">
                    {money(grandTotals.debt)}
                  </th>
                  <th className="border-b border-[#E4E3E1] bg-[#F9FAFB] px-4 py-3" />
                  <th className="border-b border-[#E4E3E1] bg-[#F9FAFB] px-4 py-3" />
                  <th className="border-b border-[#E4E3E1] bg-[#F9FAFB] px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {tree.flatMap((year) => [
                  branchRow(year, "year"),
                  ...(open.has(year.key)
                    ? year.months.flatMap((month) => [
                        branchRow(month, "month"),
                        ...(open.has(month.key)
                          ? month.invoices.map((invoice) => (
                              <tr
                                key={invoice.id}
                                className="bg-white hover:bg-gray-50"
                              >
                                <td className="border-b border-[#F2F1EF] px-4 py-3">
                                  {invoiceCheckbox(invoice)}
                                </td>
                                <td className="border-b border-[#F2F1EF] px-4 py-3 pl-16 text-sm text-gray-900">
                                  {numberCell(invoice)}
                                </td>
                                <td className="border-b border-[#F2F1EF] px-4 py-3 text-sm text-gray-900">
                                  {formatPrettyDate(invoice.date)}
                                </td>
                                <td className="border-b border-[#F2F1EF] px-4 py-3 text-right text-sm text-gray-900">
                                  {money(toNumber(invoice._amountWithVat))}
                                </td>
                                <td className="border-b border-[#F2F1EF] px-4 py-3 text-right text-sm text-gray-900">
                                  {money(toNumber(invoice._amount))}
                                </td>
                                <td className="border-b border-[#F2F1EF] px-4 py-3 text-right text-sm text-gray-900">
                                  {money(invoiceDebt(invoice))}
                                </td>
                                <td className="border-b border-[#F2F1EF] px-4 py-3 text-sm text-gray-900">
                                  {formatPrettyDate(invoice.paymentDueDate)}
                                </td>
                                <td className="border-b border-[#F2F1EF] px-4 py-3">
                                  {!!invoice.products?.length && (
                                    <button
                                      type="button"
                                      title={t("view_products")}
                                      className="inline-flex h-8 w-8 items-center justify-center rounded-sm border border-[#E6E1DB] bg-white text-[#3F3227] hover:bg-gray-50"
                                      onClick={() => onOpenProducts(invoice)}
                                    >
                                      <Eye className="h-4 w-4" />
                                    </button>
                                  )}
                                </td>
                                <td className="border-b border-[#F2F1EF] px-4 py-3">
                                  <div className="flex items-center gap-1">
                                    {rowActions(invoice, desktopActionBtnCls)}
                                  </div>
                                </td>
                              </tr>
                            ))
                          : []),
                      ])
                    : []),
                ])}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {selectionBar}
    </>
  );
};

export default InvoicesTree;
